import React, { useCallback, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActor, useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@chargify/custom-ui-components';

import { componentsSelectors, subscriptionsSelectors } from '../../machines';
import { useComponentSettings } from '../../hooks';
import { useSubscriptionIsActive } from '../SubscriptionContainer/hooks';

import { ComponentsContext, GlobalStateContext } from '../../contexts';
import SubscriptionComponents from '../../components/SubscriptionComponents';
import { SubscriptionComponentsWrapper } from './ComponentsContainer.styles';

export type ComponentsContainerProps = {
  /**
   * Subscription identifer
   */
  subscriptionId: number;
  /**
   * Product catalog identifer
   */
  catalogId: number;
};

const ComponentsContainer = ({
  subscriptionId,
  catalogId,
}: ComponentsContainerProps) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const navigate = useNavigate();
  const { subscriptionsService, modalsService } =
    useContext(GlobalStateContext);
  const { componentsRef, subscription } = useSelector(
    subscriptionsService,
    useCallback(
      subscriptionsSelectors.getSubscriptionById(catalogId, subscriptionId),
      []
    )
  );

  const subscriptionIsActive = useSubscriptionIsActive(subscription.status);

  const [componentsState, sendToMachine] = useActor(componentsRef);

  useEffect(() => {
    if (componentsState.matches('idle')) {
      sendToMachine({ type: 'FETCH_COMPONENTS' });
    }
  }, []);

  const {
    prepaid,
    metered,
    on_off: onOff,
    quantity,
  } = useSelector(
    componentsRef,
    componentsSelectors.getComponentsUsedInBilling
  );

  const componentUpdatesAllowed = useSelector(
    componentsRef,
    componentsSelectors.getComponentsUpdatesAllowed
  );

  const {
    componentsAllocation: { allocationFlow, editAllowed: customerSelfService },
  } = useComponentSettings();

  const onEditAddons = useCallback(() => {
    if (allocationFlow === 'page') {
      navigate(`/componentsAllocation/${catalogId}/${subscriptionId}`);
    } else {
      modalsService.send({
        type: 'SHOW_ADDONS_CONFIGURATION',
        catalogId,
        subscriptionId,
      });
    }
  }, [allocationFlow]);

  return (
    <ComponentsContext.Provider
      value={{ subscriptionIsActive: subscriptionIsActive }}
    >
      <SubscriptionComponentsWrapper
        marginTop={3}
        paddingTop={5}
        data-testid="subscription-components"
      >
        {componentsState.matches('loading') && (
          <SubscriptionComponents.LoadingSkeleton
            variant={subscriptionIsActive ? 'primary' : 'secondary'}
          />
        )}
        {componentsState.matches('ready') && (
          <SubscriptionComponents
            prepaid={prepaid}
            metered={metered}
            onOff={onOff}
            quantity={quantity}
            onEditAddons={onEditAddons}
            selfServiceEnabled={customerSelfService && componentUpdatesAllowed}
          />
        )}
        {componentsState.matches('error') && (
          <Alert color="danger">
            <Alert.TextMessage>
              {t(
                'components_allocations.we_were_unable_to_display_your_components'
              )}
            </Alert.TextMessage>
          </Alert>
        )}
      </SubscriptionComponentsWrapper>
    </ComponentsContext.Provider>
  );
};

export default ComponentsContainer;
